import type { Database } from '@copro/api';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createRouterLinkClickHandler } from '../../../hooks/useRouterLinkClick';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useSpacePath } from '../../../hooks/useSpacePath';
import { usePermissions } from '../../../hooks/usePermissions';
import { useGetSubTopicActiveCounts } from '../api/useGetSubTopicActiveCounts';
import { useGetSubTopicCounts } from '../api/useGetSubTopicCounts';
import { useGetSubTopicListPaged } from '../api/useGetSubTopicListPaged';
import { useGetTopicById } from '../api/useGetTopicById';
import { useGetTopicListPaged } from '../api/useGetTopicListPaged';

type Topic = Database['public']['Tables']['topics']['Row'];
type SubTopic = Database['public']['Tables']['sub_topics']['Row'];
export type StatusFilter = 'all' | 'active' | 'archived';

const DEFAULT_PER_PAGE = 15;
export const PER_PAGE_OPTIONS = [DEFAULT_PER_PAGE, 30, 60, 100];
const DEFAULT_STATUS: StatusFilter = 'active';

// The full state/handlers bundle exposed by the page hook, reused as the prop
// type for the panel sub-components.
export type TopicListPage = ReturnType<typeof useTopicListPage>;

function pluralize(count: number, singular: string, plural: string) {
  return count > 1 ? plural : singular;
}

// "4 sous-sujets actifs", "Aucun sous-sujet actif".
export function formatActiveSubTopicCount(count: number) {
  if (count === 0) {
    return 'Aucun sous-sujet actif';
  }
  return `${count} ${pluralize(count, 'sous-sujet actif', 'sous-sujets actifs')}`;
}

// "4 sous-sujets actifs · 1 archivé".
export function formatSubTopicCounts(active: number, archived: number) {
  const activeLabel = formatActiveSubTopicCount(active);
  if (archived === 0) {
    return activeLabel;
  }
  return `${activeLabel} · ${archived} ${pluralize(archived, 'archivé', 'archivés')}`;
}

export function filterTopics(
  topics: Topic[],
  search: string,
  statusFilter: StatusFilter
): Topic[] {
  const query = search.trim().toLowerCase();
  return topics.filter((t) => {
    const matchesSearch = !query || t.name.toLowerCase().includes(query);
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && !t.archived) ||
      (statusFilter === 'archived' && t.archived);
    return matchesSearch && matchesStatus;
  });
}

export function filterSubTopics(
  subTopics: SubTopic[],
  search: string,
  statusFilter: StatusFilter
): SubTopic[] {
  const query = search.trim().toLowerCase();
  return subTopics.filter((s) => {
    const matchesSearch = !query || s.name.toLowerCase().includes(query);
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && !s.archived_at) ||
      (statusFilter === 'archived' && !!s.archived_at);
    return matchesSearch && matchesStatus;
  });
}

export function useTopicListPage() {
  const { topicId } = useParams<{ topicId?: string }>();
  const navigate = useNavigate();
  const spacePath = useSpacePath();
  const { isAdmin } = usePermissions();

  // Search/Filters/Pagination — persisted in URL search params so browser
  // back/forward restores the list exactly as it was.
  const [searchParams, setSearchParams] = useSearchParams();

  // Read a single param with a fallback to its default value.
  function getParam(key: string, fallback: string): string {
    return searchParams.get(key) ?? fallback;
  }

  // Update one param (replace history so typing does not flood it). Params
  // equal to their default are dropped to keep the URL clean.
  const updateParam = useCallback(
    (key: string, value: string, defaultValue: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === defaultValue) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const topicSearch = getParam('q', '');
  const topicStatusFilter = getParam('st', DEFAULT_STATUS) as StatusFilter;
  const topicPage = Number(getParam('tp', '1')) || 1;
  const topicPerPage = Number(getParam('tpp', String(DEFAULT_PER_PAGE))) || DEFAULT_PER_PAGE;

  const subSearch = getParam('sq', '');
  const subStatusFilter = getParam('ss', DEFAULT_STATUS) as StatusFilter;
  const subPage = Number(getParam('sp', '1')) || 1;
  const subPerPage = Number(getParam('spp', String(DEFAULT_PER_PAGE))) || DEFAULT_PER_PAGE;

  function setTopicSearch(value: string) {
    updateParam('q', value, '');
  }
  function setTopicStatusFilter(value: StatusFilter) {
    updateParam('st', value, DEFAULT_STATUS);
  }
  function setTopicPage(value: number) {
    updateParam('tp', String(value), '1');
  }
  function setTopicPerPage(value: number) {
    updateParam('tpp', String(value), String(DEFAULT_PER_PAGE));
  }
  function setSubSearch(value: string) {
    updateParam('sq', value, '');
  }
  function setSubStatusFilter(value: StatusFilter) {
    updateParam('ss', value, DEFAULT_STATUS);
  }
  function setSubPage(value: number) {
    updateParam('sp', String(value), '1');
  }
  function setSubPerPage(value: number) {
    updateParam('spp', String(value), String(DEFAULT_PER_PAGE));
  }

  // Drop several params in one update: successive setSearchParams calls in
  // the same tick would each start from the same stale params.
  function clearParams(keysToClear: string[]) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        keysToClear.forEach((key) => next.delete(key));
        return next;
      },
      { replace: true }
    );
  }
  function resetTopicFilters() {
    clearParams(['q', 'st', 'tp']);
  }
  function resetSubFilters() {
    clearParams(['sq', 'ss', 'sp']);
  }

  // Modals state
  const [isNewTopicModalOpen, setIsNewTopicModalOpen] = useState(false);
  const [isNewSubTopicModalOpen, setIsNewSubTopicModalOpen] = useState(false);

  // Debounce search inputs so we issue one request after typing settles,
  // not one per keystroke (the input itself stays instant via the URL).
  const debouncedTopicSearch = useDebouncedValue(topicSearch);
  const debouncedSubSearch = useDebouncedValue(subSearch);

  // Reset page numbers on filter changes
  useEffect(() => {
    setTopicPage(1);
  }, [debouncedTopicSearch, topicStatusFilter, topicPerPage]);

  useEffect(() => {
    setSubPage(1);
  }, [debouncedSubSearch, subStatusFilter, topicId, subPerPage]);

  // Fetch the current page of topics (server-side pagination + filtering)
  const topicQuery = useGetTopicListPaged({
    status: topicStatusFilter,
    search: debouncedTopicSearch,
    page: topicPage,
    perPage: topicPerPage,
  });
  const topics = topicQuery.data?.rows ?? [];
  const totalTopics = topicQuery.data?.total ?? 0;

  // Active sub-topic count per visible topic — one batched request for the
  // whole page instead of one query per row.
  const { data: activeCountByTopic = {} } = useGetSubTopicActiveCounts(
    topics.map((t) => t.id)
  );

  // Fetch the current page of sub-topics for the active topic
  const subTopicQuery = useGetSubTopicListPaged({
    topicId: topicId || '',
    status: subStatusFilter,
    search: debouncedSubSearch,
    page: subPage,
    perPage: subPerPage,
  });
  const subTopics = subTopicQuery.data?.rows ?? [];
  const totalSubTopics = subTopicQuery.data?.total ?? 0;

  // Active topic — fetched individually since it may not be on the current page
  const selectedTopicQuery = useGetTopicById(topicId);
  const selectedTopic = selectedTopicQuery.data ?? null;

  // Header counts (active/archived) across all sub-topics of the active topic
  const { data: subCounts } = useGetSubTopicCounts(topicId);
  const subActiveCount = subCounts?.active ?? 0;
  const subArchivedCount = subCounts?.archived ?? 0;

  const hasTopicFilters = topicSearch.trim() !== '' || topicStatusFilter !== DEFAULT_STATUS;
  const hasSubFilters = subSearch.trim() !== '' || subStatusFilter !== DEFAULT_STATUS;
  const query = searchParams.toString();
  const listHref = `${spacePath('/topics-management')}${query ? `?${query}` : ''}`;

  function getTopicHref(id: string) {
    return `${spacePath(`/topics-management/${id}`)}${query ? `?${query}` : ''}`;
  }

  function getTopicClickHandler(id: string) {
    return createRouterLinkClickHandler(navigate, getTopicHref(id));
  }

  function getSubTopicHref(id: string) {
    return spacePath(`/topics/${id}`);
  }

  function getSubTopicClickHandler(id: string) {
    return createRouterLinkClickHandler(navigate, getSubTopicHref(id));
  }

  function handleTopicCreated(topic: Topic) {
    navigate(spacePath(`/topics-management/${topic.id}`));
  }

  // Stay on the topic and show the new sub-topic from the first page.
  function handleSubTopicCreated() {
    setSubPage(1);
  }

  // Leaving the detail of a deleted topic: nothing is left to show there.
  function handleTopicDeleted(id: string) {
    if (id === topicId) {
      navigate(listHref);
    }
  }

  return {
    isAdmin,
    topicId,

    // Lists — already paginated and filtered server-side
    topics,
    activeCountByTopic,
    subTopics,
    selectedTopic,
    totalTopics,
    totalSubTopics,
    // A single short page needs no pagination.
    showTopicPagination: totalTopics > PER_PAGE_OPTIONS[0],
    showSubPagination: totalSubTopics > PER_PAGE_OPTIONS[0],
    subCountsLabel: formatSubTopicCounts(subActiveCount, subArchivedCount),
    hasSubTopics: subActiveCount + subArchivedCount > 0,

    // Loading / error states
    isTopicsLoading: topicQuery.isLoading,
    isTopicsError: topicQuery.isError,
    isSubTopicsLoading: subTopicQuery.isLoading,
    isSubTopicsError: subTopicQuery.isError,
    isSelectedTopicLoading: selectedTopicQuery.isLoading,

    // Topic Filters
    topicSearch,
    setTopicSearch,
    topicStatusFilter,
    setTopicStatusFilter,
    topicPage,
    setTopicPage,
    topicPerPage,
    setTopicPerPage,
    hasTopicFilters,
    resetTopicFilters,

    // Subtopic Filters
    subSearch,
    setSubSearch,
    subStatusFilter,
    setSubStatusFilter,
    subPage,
    setSubPage,
    subPerPage,
    setSubPerPage,
    hasSubFilters,
    resetSubFilters,

    // Navigation
    listHref,
    getTopicHref,
    getTopicClickHandler,
    getSubTopicHref,
    getSubTopicClickHandler,
    handleTopicDeleted,

    // Modals
    isNewTopicModalOpen,
    handleOpenNewTopicModal: () => setIsNewTopicModalOpen(true),
    handleCloseNewTopicModal: () => setIsNewTopicModalOpen(false),
    handleTopicCreated,
    isNewSubTopicModalOpen,
    setIsNewSubTopicModalOpen,
    handleCloseNewSubTopicModal: () => setIsNewSubTopicModalOpen(false),
    handleSubTopicCreated,
  };
}
